import { useState } from 'react';
import {
    Container,
    Typography,
    Box,
    Paper,
    TextField,
    Button,
    Alert,
    Divider,
    Chip,
    CircularProgress,
} from '@mui/material';
import { Star as StarIcon, OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { subscriptions } from '../services/api';

const Settings = () => {
    const { user, updateUser } = useAuth();

    const [profile, setProfile] = useState({
        firstName: user?.firstName || '',
        lastName: user?.lastName || '',
        email: user?.email || '',
    });
    const [profileSuccess, setProfileSuccess] = useState('');
    const [profileError, setProfileError] = useState('');

    const [passwords, setPasswords] = useState({
        newPassword: '',
        confirmPassword: '',
    });
    const [passwordSuccess, setPasswordSuccess] = useState('');
    const [passwordError, setPasswordError] = useState('');

    const [portalLoading, setPortalLoading] = useState(false);
    const [portalError, setPortalError] = useState('');

    const isPremium = user?.tier === 'PREMIUM';

    const handleProfileChange = (e) => {
        setProfile({ ...profile, [e.target.name]: e.target.value });
    };

    const handlePasswordChange = (e) => {
        setPasswords({ ...passwords, [e.target.name]: e.target.value });
    };

    const handleProfileSubmit = async (e) => {
        e.preventDefault();
        setProfileError('');
        setProfileSuccess('');
        try {
            await api.put(`/users/${user.id}/profile`, {
                firstName: profile.firstName,
                lastName: profile.lastName,
                email: profile.email,
            });
            updateUser({ firstName: profile.firstName, lastName: profile.lastName, email: profile.email });
            setProfileSuccess('Profile updated successfully.');
        } catch (err) {
            setProfileError(err.response?.data?.message || err.response?.data || 'Failed to update profile.');
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        setPasswordError('');
        setPasswordSuccess('');
        if (passwords.newPassword !== passwords.confirmPassword) {
            setPasswordError('New passwords do not match.');
            return;
        }
        if (passwords.newPassword.length < 6) {
            setPasswordError('Password must be at least 6 characters.');
            return;
        }
        try {
            await api.put(`/users/${user.id}`, {
                ...user,
                password: passwords.newPassword,
            });
            setPasswordSuccess('Password updated successfully.');
            setPasswords({ newPassword: '', confirmPassword: '' });
        } catch (err) {
            setPasswordError(err.response?.data?.message || err.response?.data || 'Failed to update password.');
        }
    };

    const handleUpgrade = async () => {
        setPortalLoading(true);
        setPortalError('');
        try {
            const res = await subscriptions.checkout();
            window.location.href = res.data.url;
        } catch (err) {
            setPortalError(err.response?.data?.message || 'Could not start checkout. Please try again.');
            setPortalLoading(false);
        }
    };

    const handleManageBilling = async () => {
        setPortalLoading(true);
        setPortalError('');
        try {
            const res = await subscriptions.portal();
            window.location.href = res.data.url;
        } catch (err) {
            setPortalError(err.response?.data?.message || 'Could not open billing portal. Please try again.');
            setPortalLoading(false);
        }
    };

    return (
        <Container maxWidth="sm" sx={{ mt: 4, mb: 4 }}>
            <Typography variant="h4" component="h1" fontWeight={600} sx={{ mb: 4 }}>
                Settings
            </Typography>

            {/* Subscription section */}
            <Paper elevation={2} sx={{ p: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                    <Typography variant="h6">Subscription</Typography>
                    {isPremium && (
                        <Chip
                            icon={<StarIcon sx={{ fontSize: 14 }} />}
                            label="Premium"
                            size="small"
                            sx={{ bgcolor: '#FFD700', color: '#000', fontWeight: 700 }}
                        />
                    )}
                </Box>
                <Divider sx={{ mb: 3 }} />
                {portalError && <Alert severity="error" sx={{ mb: 2 }}>{portalError}</Alert>}
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {isPremium
                        ? 'You are on the Premium plan ($9/month). You have unlimited availability slots.'
                        : 'You are on the Free plan. Upgrade to Premium to unlock unlimited availability slots.'}
                </Typography>
                {isPremium ? (
                    <Button
                        variant="outlined"
                        onClick={handleManageBilling}
                        disabled={portalLoading}
                        endIcon={portalLoading ? <CircularProgress size={14} /> : <OpenInNewIcon fontSize="small" />}
                    >
                        Manage Billing
                    </Button>
                ) : (
                    <Button
                        variant="contained"
                        onClick={handleUpgrade}
                        disabled={portalLoading}
                        startIcon={portalLoading ? <CircularProgress size={14} /> : <StarIcon />}
                    >
                        Upgrade to Premium — $9/mo
                    </Button>
                )}
            </Paper>

            {/* Profile section */}
            <Paper elevation={2} sx={{ p: 4, mb: 4 }}>
                <Typography variant="h6" gutterBottom>
                    Profile
                </Typography>
                <Divider sx={{ mb: 3 }} />
                {profileSuccess && <Alert severity="success" sx={{ mb: 2 }}>{profileSuccess}</Alert>}
                {profileError && <Alert severity="error" sx={{ mb: 2 }}>{profileError}</Alert>}
                <Box component="form" onSubmit={handleProfileSubmit}>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="First Name"
                        name="firstName"
                        value={profile.firstName}
                        onChange={handleProfileChange}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Last Name"
                        name="lastName"
                        value={profile.lastName}
                        onChange={handleProfileChange}
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Email Address"
                        name="email"
                        type="email"
                        value={profile.email}
                        onChange={handleProfileChange}
                        required
                    />
                    <Button type="submit" variant="contained" sx={{ mt: 3 }}>
                        Save Profile
                    </Button>
                </Box>
            </Paper>

            {/* Password section */}
            <Paper elevation={2} sx={{ p: 4 }}>
                <Typography variant="h6" gutterBottom>
                    Change Password
                </Typography>
                <Divider sx={{ mb: 3 }} />
                {passwordSuccess && <Alert severity="success" sx={{ mb: 2 }}>{passwordSuccess}</Alert>}
                {passwordError && <Alert severity="error" sx={{ mb: 2 }}>{passwordError}</Alert>}
                <Box component="form" onSubmit={handlePasswordSubmit}>
                    <TextField
                        fullWidth
                        margin="normal"
                        label="New Password"
                        name="newPassword"
                        type="password"
                        value={passwords.newPassword}
                        onChange={handlePasswordChange}
                        required
                    />
                    <TextField
                        fullWidth
                        margin="normal"
                        label="Confirm New Password"
                        name="confirmPassword"
                        type="password"
                        value={passwords.confirmPassword}
                        onChange={handlePasswordChange}
                        required
                    />
                    <Button type="submit" variant="contained" sx={{ mt: 3 }}>
                        Update Password
                    </Button>
                </Box>
            </Paper>
        </Container>
    );
};

export default Settings;
